/**
 * ResolveIssuesWizard — three-step takeover for the bulk "Resolve issues" flow.
 *
 *   1. Review    — the sections + issue counts selected in the Issues tab.
 *   2. Reference — multi-select documents from the run's bound libraries to
 *                  scope retrieval to (empty = search the whole library).
 *   3. Confirm   — summary + Start; large batches get a heads-up.
 *
 * On Start it hands the chosen document scope to the shell, which runs
 * ``useResolveIssuesFlow.startResolve`` (live on the spine).
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useMemo, useState } from "react";
import type { ResolveIssueDoc } from "../../api/client";
import type { ResolveSelectionTarget } from "./SubmissionIssuesTab";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

/** Minimal document shape for the picker — matches the library inventory's
 *  ``DocumentInfo`` (id + name), the same data the Library Hub panel renders. */
export interface WizardDocument {
  id: string;
  name: string;
}

interface ResolveIssuesWizardProps {
  targets: ResolveSelectionTarget[];
  /** The run's library documents, pre-loaded from the library inventory (same
   *  source as the Library Hub panel). Empty = retrieval uses the whole library. */
  documents: WizardDocument[];
  busy?: boolean;
  onCancel: () => void;
  /** Start resolving with the selected reference-doc scope (empty = all docs). */
  onStart: (documentScope: ResolveIssueDoc[]) => void;
}

const LARGE_BATCH = 50;

const useStyles = makeStyles({
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 40,
    background: "rgba(20,22,34,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  card: {
    width: "100%",
    maxWidth: "460px",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    background: surfaceColors.paper,
    borderRadius: "10px",
    boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
    overflow: "hidden",
    fontFamily: fonts.body,
  },
  header: {
    padding: "14px 16px 10px",
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  title: { fontSize: "14px", fontWeight: 700, color: inkColors.ink, margin: 0 },
  steps: { fontSize: "10.5px", color: inkColors.ink4, marginTop: "3px", letterSpacing: "0.3px" },
  body: { padding: "12px 16px", overflowY: "auto", flex: "1 1 auto" },
  sectionRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    padding: "5px 0",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontSize: "12px",
  },
  secNum: { fontFamily: fonts.mono, fontWeight: 700, color: inkColors.ink2, minWidth: "48px" },
  secTitle: { color: inkColors.ink3, flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  secCount: { color: "#B71C1C", fontWeight: 700, fontSize: "11px" },
  docRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 0",
    fontSize: "12px",
    color: inkColors.ink2,
  },
  checkbox: { cursor: "pointer", accentColor: brandColors.primary, flex: "0 0 auto" },
  selectAllRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 0",
    marginBottom: "2px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink2,
    cursor: "pointer",
  },
  hint: { fontSize: "11px", color: inkColors.ink4, margin: "2px 0 10px", lineHeight: 1.4 },
  warn: {
    fontSize: "11.5px",
    color: "#8A6D00",
    background: "#FFF8E6",
    border: "1px solid #F0DFA8",
    borderRadius: "6px",
    padding: "8px 10px",
    marginTop: "8px",
  },
  summary: { fontSize: "12.5px", color: inkColors.ink2, lineHeight: 1.6 },
  strong: { fontWeight: 700, color: inkColors.ink },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderTop: `1px solid ${surfaceColors.line}`,
  },
  spacer: { flex: "1 1 auto" },
  ghostBtn: {
    border: "none",
    background: "transparent",
    color: inkColors.ink3,
    fontFamily: fonts.body,
    fontSize: "12px",
    cursor: "pointer",
    padding: "6px 10px",
    borderRadius: "5px",
    ":hover": { background: "#F0F0F0" },
  },
  primaryBtn: {
    border: "none",
    borderRadius: "6px",
    background: brandColors.primary,
    color: "#FFFFFF",
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 700,
    padding: "7px 16px",
    cursor: "pointer",
    ":disabled": { background: "#B7C4D6", cursor: "default" },
  },
});

export const ResolveIssuesWizard: React.FC<ResolveIssuesWizardProps> = ({
  targets,
  documents,
  busy = false,
  onCancel,
  onStart,
}) => {
  const styles = useStyles();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());

  const totalIssues = useMemo(() => targets.reduce((n, t) => n + t.issue_ids.length, 0), [targets]);

  // Documents come pre-loaded from the shell's library inventory (same source
  // as the Library Hub panel) — de-dup by id, no fetch, no spinner.
  const docs = useMemo(() => {
    const byId = new Map<string, WizardDocument>();
    for (const d of documents) if (d.id) byId.set(d.id, d);
    return [...byId.values()];
  }, [documents]);

  const toggleDoc = (id: string) =>
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Select-all: clear when every doc is already ticked, else tick them all.
  const allDocsSelected = docs.length > 0 && docs.every((d) => selectedDocs.has(d.id));
  const someDocsSelected = selectedDocs.size > 0 && !allDocsSelected;
  const toggleAllDocs = () => setSelectedDocs(allDocsSelected ? new Set() : new Set(docs.map((d) => d.id)));

  const documentScope: ResolveIssueDoc[] = docs
    .filter((d) => selectedDocs.has(d.id))
    .map((d) => ({ doc_id: d.id, name: d.name }));

  const scopeLabel = documentScope.length === 0 ? "all documents in the library" : `${documentScope.length} selected document(s)`;

  return (
    <div className={styles.overlay}>
      <div className={styles.card} role="dialog" aria-label="Resolve issues">
        <div className={styles.header}>
          <h2 className={styles.title}>Resolve issues with asthra</h2>
          <div className={styles.steps}>
            Step {step} of 3 · {step === 1 ? "Review" : step === 2 ? "Reference documents" : "Confirm"}
          </div>
        </div>

        <div className={styles.body}>
          {step === 1 ? (
            <>
              <div className={styles.hint}>
                asthra will re-draft these sections, filling the selected gap / regulatory issues from the
                library and leaving the rest of each section unchanged.
              </div>
              {targets.map((t) => (
                <div key={t.module_id} className={styles.sectionRow}>
                  <span className={styles.secNum}>{t.number}</span>
                  <span className={styles.secTitle}>{t.title}</span>
                  <span className={styles.secCount}>{t.issue_ids.length}</span>
                </div>
              ))}
            </>
          ) : null}

          {step === 2 ? (
            <>
              <div className={styles.hint}>
                Pick which library documents asthra should use as references. Leave all unchecked to search the
                whole library.
              </div>
              {docs.length === 0 ? (
                <div className={styles.hint}>No documents found — retrieval will use the whole library.</div>
              ) : (
                <label className={styles.selectAllRow}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={allDocsSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someDocsSelected;
                    }}
                    onChange={toggleAllDocs}
                    aria-label="Select all documents"
                  />
                  Select all documents
                </label>
              )}
              {docs.map((d) => (
                <label key={d.id} className={styles.docRow}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selectedDocs.has(d.id)}
                    onChange={() => toggleDoc(d.id)}
                  />
                  {d.name}
                </label>
              ))}
            </>
          ) : null}

          {step === 3 ? (
            <div className={styles.summary}>
              Resolving <span className={styles.strong}>{totalIssues}</span> issue(s) across{" "}
              <span className={styles.strong}>{targets.length}</span> section(s), using{" "}
              <span className={styles.strong}>{scopeLabel}</span>.
              <br />
              This runs live on the Spine tab; only the flagged locations change.
              {totalIssues > LARGE_BATCH ? (
                <div className={styles.warn}>
                  That&apos;s a large batch — resolving {totalIssues} issues can take a while and uses model
                  calls per issue. You can narrow the selection if needed.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={styles.footer}>
          {step > 1 ? (
            <button type="button" className={styles.ghostBtn} onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}>
              Back
            </button>
          ) : null}
          <button type="button" className={styles.ghostBtn} onClick={onCancel}>
            Cancel
          </button>
          <div className={styles.spacer} />
          {step < 3 ? (
            <button type="button" className={styles.primaryBtn} onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}>
              Next
            </button>
          ) : (
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={busy || totalIssues === 0}
              onClick={() => onStart(documentScope)}
            >
              {busy ? "Starting…" : "Start resolving"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResolveIssuesWizard;
